// NMEA 0183 GGA + RMC encode/parse. The simulator emits these; the real serial source parses the
// identical sentences from the u-blox receiver — so the wire format is the contract and the parser
// never changes when hardware arrives. We deliberately implement encode (which off-the-shelf parsers
// don't) so the simulator and the parser are tested against each other (round-trip).

import { FixQuality, type GnssFix } from './types';

const KNOTS_TO_MS = 0.514444;
const MS_TO_KNOTS = 1 / KNOTS_TO_MS;

/** NMEA checksum: XOR of all bytes between '$' and '*'. Returns 2-char uppercase hex. */
export function nmeaChecksum(body: string): string {
  let c = 0;
  for (let i = 0; i < body.length; i++) c ^= body.charCodeAt(i);
  return c.toString(16).toUpperCase().padStart(2, '0');
}

/** Validate a full sentence "$...*HH". */
export function validateChecksum(sentence: string): boolean {
  const s = sentence.trim();
  const star = s.lastIndexOf('*');
  if (!s.startsWith('$') || star < 0) return false;
  const body = s.slice(1, star);
  const given = s
    .slice(star + 1)
    .trim()
    .toUpperCase();
  return nmeaChecksum(body) === given;
}

function wrap(body: string): string {
  return `$${body}*${nmeaChecksum(body)}`;
}

function formatLat(lat: number): [string, 'N' | 'S'] {
  const hemi: 'N' | 'S' = lat >= 0 ? 'N' : 'S';
  const a = Math.abs(lat);
  const deg = Math.floor(a);
  const min = (a - deg) * 60;
  return [`${String(deg).padStart(2, '0')}${min.toFixed(5).padStart(8, '0')}`, hemi];
}

function formatLon(lon: number): [string, 'E' | 'W'] {
  const hemi: 'E' | 'W' = lon >= 0 ? 'E' : 'W';
  const a = Math.abs(lon);
  const deg = Math.floor(a);
  const min = (a - deg) * 60;
  return [`${String(deg).padStart(3, '0')}${min.toFixed(5).padStart(8, '0')}`, hemi];
}

function parseCoord(field?: string, hemi?: string): number | null {
  if (!field) return null;
  const v = parseFloat(field);
  if (Number.isNaN(v)) return null;
  const deg = Math.floor(v / 100);
  const min = v - deg * 100;
  let dec = deg + min / 60;
  if (hemi === 'S' || hemi === 'W') dec = -dec;
  return dec;
}

function utcTime(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}.${String(
    Math.floor(d.getUTCMilliseconds() / 10),
  ).padStart(2, '0')}`;
}

function utcDate(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}${p(d.getUTCMonth() + 1)}${p(d.getUTCFullYear() % 100)}`;
}

function parseDateTime(date?: string, time?: string): number | null {
  if (!date || !time || date.length < 6 || time.length < 6) return null;
  const dd = +date.slice(0, 2);
  const mo = +date.slice(2, 4);
  const yy = +date.slice(4, 6);
  const hh = +time.slice(0, 2);
  const mi = +time.slice(2, 4);
  const ss = +time.slice(4, 6);
  if ([dd, mo, yy, hh, mi, ss].some(Number.isNaN)) return null;
  return Date.UTC(2000 + yy, mo - 1, dd, hh, mi, ss);
}

/** Encode a GGA sentence (position + fix quality). `talker` defaults to GN (multi-constellation). */
export function encodeGGA(fix: GnssFix, talker = 'GN'): string {
  const [lat, ns] = formatLat(fix.lat);
  const [lon, ew] = formatLon(fix.lon);
  const body =
    `${talker}GGA,${utcTime(fix.timestamp)},${lat},${ns},${lon},${ew},` +
    `${fix.quality},${String(fix.satellites).padStart(2, '0')},${fix.hdop.toFixed(1)},` +
    `${(fix.altitude ?? 0).toFixed(1)},M,0.0,M,,`;
  return wrap(body);
}

/** Encode an RMC sentence (position + speed + course + date). */
export function encodeRMC(fix: GnssFix, talker = 'GN'): string {
  const [lat, ns] = formatLat(fix.lat);
  const [lon, ew] = formatLon(fix.lon);
  const status = fix.quality === FixQuality.Invalid ? 'V' : 'A';
  const speedKn = ((fix.speed ?? 0) * MS_TO_KNOTS).toFixed(2);
  const track = (fix.heading ?? 0).toFixed(1);
  const body =
    `${talker}RMC,${utcTime(fix.timestamp)},${status},${lat},${ns},${lon},${ew},` +
    `${speedKn},${track},${utcDate(fix.timestamp)},,,A`;
  return wrap(body);
}

function fields(sentence: string): string[] | null {
  if (!validateChecksum(sentence)) return null;
  const star = sentence.trim().lastIndexOf('*');
  return sentence.trim().slice(1, star).split(',');
}

/** Parse a GGA sentence into the fields it carries. Returns null on bad checksum / wrong type. */
export function parseGGA(sentence: string): Partial<GnssFix> | null {
  const f = fields(sentence);
  if (!f || !/GGA$/.test(f[0] ?? '')) return null;
  const lat = parseCoord(f[2], f[3]);
  const lon = parseCoord(f[4], f[5]);
  if (lat === null || lon === null) return null;
  const out: Partial<GnssFix> = {
    lat,
    lon,
    quality: (parseInt(f[6] ?? '', 10) || 0) as FixQuality,
    satellites: parseInt(f[7] ?? '', 10) || 0,
    hdop: parseFloat(f[8] ?? '') || 0,
  };
  const alt = f[9] ? parseFloat(f[9]) : NaN;
  if (!Number.isNaN(alt)) out.altitude = alt;
  return out;
}

/** Parse an RMC sentence into the fields it carries. Returns null on bad checksum / wrong type. */
export function parseRMC(sentence: string): Partial<GnssFix> | null {
  const f = fields(sentence);
  if (!f || !/RMC$/.test(f[0] ?? '')) return null;
  const lat = parseCoord(f[3], f[4]);
  const lon = parseCoord(f[5], f[6]);
  const out: Partial<GnssFix> = {};
  if (lat !== null) out.lat = lat;
  if (lon !== null) out.lon = lon;
  const speed = f[7] ? parseFloat(f[7]) * KNOTS_TO_MS : NaN;
  if (!Number.isNaN(speed)) out.speed = speed;
  const heading = f[8] ? parseFloat(f[8]) : NaN;
  if (!Number.isNaN(heading)) out.heading = heading;
  const ts = parseDateTime(f[9], f[1]);
  if (ts !== null) out.timestamp = ts;
  return out;
}

/** Parse any supported sentence (GGA or RMC) into the fields it carries. */
export function parseNmea(sentence: string): Partial<GnssFix> | null {
  if (/GGA/.test(sentence)) return parseGGA(sentence);
  if (/RMC/.test(sentence)) return parseRMC(sentence);
  return null;
}

/**
 * Assembles a stream of NMEA lines into complete fixes. A GGA line (which carries position + quality)
 * triggers emission, merged with the most recent RMC fields (speed/heading/timestamp).
 */
export class NmeaFixAssembler {
  private partial: Partial<GnssFix> = {};

  push(line: string, fallbackTimestamp: number): GnssFix | null {
    const p = parseNmea(line);
    if (!p) return null;
    this.partial = { ...this.partial, ...p };
    if (!/GGA/.test(line)) return null;
    const { lat, lon } = this.partial;
    if (lat === undefined || lon === undefined) return null;
    return {
      timestamp: this.partial.timestamp ?? fallbackTimestamp,
      lat,
      lon,
      quality: this.partial.quality ?? FixQuality.Invalid,
      satellites: this.partial.satellites ?? 0,
      hdop: this.partial.hdop ?? 99,
      altitude: this.partial.altitude,
      speed: this.partial.speed,
      heading: this.partial.heading,
    };
  }
}
